import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription, timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { TerminalPaymentAttempt, TerminalPaymentService } from './terminal-payment.service';

export interface TerminalPaymentWaitDialogData {
  attemptId: number;
  title?: string;
}

@Component({
  selector: 'app-terminal-payment-wait-dialog',
  template: `
    <h2 mat-dialog-title>{{ title }}</h2>
    <mat-dialog-content class="terminalPaymentDialog">
      <div class="terminalPaymentStatus" *ngIf="attempt">
        <mat-spinner *ngIf="!isComplete" [diameter]="42"></mat-spinner>
        <mat-icon
          *ngIf="attempt.status === 'APPROVED' && bookingStatus !== 'FAILED' && isComplete"
          color="primary"
          >check_circle</mat-icon
        >
        <mat-icon
          *ngIf="isComplete && (attempt.status !== 'APPROVED' || bookingStatus === 'FAILED')"
          color="warn"
          >error</mat-icon
        >
        <div>
          <div class="terminalPaymentAmount">
            {{ attempt.amount | number: '1.2-2' }} {{ attempt.currency || 'UAH' }}
          </div>
          <div class="terminalPaymentText">{{ statusText }}</div>
          <small *ngIf="attempt.responseCode">Код: {{ attempt.responseCode }}</small>
          <small *ngIf="attempt.error">{{ attempt.error }}</small>
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button
        mat-button
        type="button"
        [disabled]="closing || (attempt?.status === 'APPROVED' && !isComplete)"
        (click)="close()"
      >
        {{ isComplete ? 'Закрыть' : closing ? 'Отменяем...' : 'Отменить' }}
      </button>
    </mat-dialog-actions>
  `,
  styles: [
    `
      .terminalPaymentDialog {
        min-width: 320px;
      }

      .terminalPaymentStatus {
        display: flex;
        gap: 16px;
        align-items: center;
        padding: 8px 0 4px;
      }

      .terminalPaymentStatus mat-icon {
        font-size: 42px;
        width: 42px;
        height: 42px;
      }

      .terminalPaymentAmount {
        font-size: 20px;
        font-weight: 600;
      }

      .terminalPaymentText {
        margin-top: 4px;
      }

      small {
        display: block;
        margin-top: 4px;
        color: #667085;
      }
    `
  ]
})
export class TerminalPaymentWaitDialogComponent implements OnInit, OnDestroy {
  attempt?: TerminalPaymentAttempt;

  closing = false;

  private subscription?: Subscription;

  private closeTimer?: ReturnType<typeof setTimeout>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: TerminalPaymentWaitDialogData,
    private readonly dialogRef: MatDialogRef<TerminalPaymentWaitDialogComponent>,
    private readonly terminalPaymentService: TerminalPaymentService
  ) {}

  get title() {
    return this.data.title || 'Оплата через терминал';
  }

  get statusText() {
    switch (this.attempt?.status) {
      case 'APPROVED':
        if (this.bookingStatus === 'PENDING') {
          return 'Оплата подтверждена, записываем её в CRM...';
        }
        if (this.bookingStatus === 'FAILED') {
          return this.attempt.bookingError || 'Оплата подтверждена, но не записана в CRM';
        }
        return 'Оплата подтверждена';
      case 'CANCELLED':
        return 'Оплата отменена';
      case 'DECLINED':
        return this.attempt.responseMessage || 'Оплата отклонена';
      case 'TIMEOUT':
        return 'Истекло время ожидания терминала';
      case 'ERROR':
        return this.attempt.error || this.attempt.responseMessage || 'Ошибка оплаты через терминал';
      default:
        return 'Ожидание ответа терминала...';
    }
  }

  get bookingStatus() {
    return this.attempt?.booking_status || this.attempt?.bookingStatus;
  }

  get isComplete() {
    if (!this.attempt) return false;
    if (this.attempt.status !== 'APPROVED') return this.attempt.isFinal;
    return ['NOT_REQUIRED', 'BOOKED', 'FAILED'].includes(String(this.bookingStatus || ''));
  }

  ngOnInit() {
    this.subscription = timer(0, 1500)
      .pipe(switchMap(() => this.terminalPaymentService.getAttempt(this.data.attemptId)))
      .subscribe((attempt) => {
        this.attempt = attempt;
        if (this.isComplete && !this.closeTimer) {
          this.closeTimer = setTimeout(() => this.close(), 1400);
        }
      });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
    }
  }

  close() {
    if (this.closing) {
      return;
    }

    if (!this.attempt || this.isComplete) {
      this.dialogRef.close(this.attempt);
      return;
    }

    if (this.attempt.status === 'APPROVED') return;

    this.closing = true;
    this.terminalPaymentService.cancelAttempt(this.data.attemptId).subscribe(
      (attempt) => {
        this.attempt = attempt;
        this.dialogRef.close(attempt);
      },
      () => {
        this.dialogRef.close(this.attempt);
      }
    );
  }
}
